import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Row, Col, Button, Table, Spinner, Alert, Form, Modal } from 'react-bootstrap';
import { usePermissions } from '../../../hooks/usePermissions';
import { usePortalApiRequest } from '../../../redux/api/portalApi';
import { selectApiUrls } from '../../../redux/slices/apiUrlSlice';
import { selectUserDetails, selectUserRoles } from '../../../redux/slices/userSlice';
import { createSalesEndpoints } from '../api/salesApi';
import { isDeletedSalesRow } from '../hooks/useSalesApiResource';
import SalesProjectSharePointTabs from './SalesProjectSharePointTabs';
const fallbackStates = ['WI', 'CA', 'TX', 'FL'];
const fallbackMarkets = ['Landscape', 'Agriculture', 'Municipal'];
const fallbackConfidence = [25, 50, 75, 100];
const AGE_WARNING_DAYS = 30;
const PROJECT_STATUS_LOOKUP_TYPE_ID = 38;
const SALES_QUOTE_STATUS_LOOKUP_TYPE_ID = 17;
const LEGACY_PROJECT_STATUS_OPTIONS = [{
  id: 1,
  label: 'Open'
}, {
  id: 2,
  label: 'Sold'
}, {
  id: 3,
  label: 'Lost'
}];
const calculateAgeInDays = dateValue => {
  if (!dateValue) {
    return null;
  }
  const parsedDate = new Date(dateValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }
  const now = new Date();
  const milliseconds = now.getTime() - parsedDate.getTime();
  return Math.max(0, Math.floor(milliseconds / (1000 * 60 * 60 * 24)));
};
const toDateOrNull = value => {
  if (!value) {
    return null;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed;
};
const applyDerivedProjectAge = (project, projectQuotes) => {
  const projectCreatedDate = project?.createdDate ?? project?.CreatedDate ?? project?.createdOn ?? project?.CreatedOn ?? null;
  const quoteDates = (projectQuotes || []).map(quote => toDateOrNull(quote?.createdDate ?? quote?.CreatedDate)).filter(date => Boolean(date));
  const earliestQuoteDate = quoteDates.length > 0 ? new Date(Math.min(...quoteDates.map(date => date.getTime()))).toISOString() : null;
  const effectiveCreatedDate = projectCreatedDate || earliestQuoteDate;
  const age = calculateAgeInDays(effectiveCreatedDate);
  return {
    ...project,
    createdDate: effectiveCreatedDate,
    age,
    ageFlag: Boolean(typeof age === 'number' && age > AGE_WARNING_DAYS)
  };
};
const normalizeCompany = (company, companyId) => {
  if (!company && !companyId) {
    return null;
  }
  return {
    id: Number(company?.id ?? company?.Id ?? company?.companyId ?? company?.CompanyId ?? companyId ?? 0),
    name: String(company?.name ?? company?.Name ?? company?.companyName ?? company?.CompanyName ?? `${company?.firstName ?? company?.FirstName ?? ''} ${company?.lastName ?? company?.LastName ?? ''}`.trim() ?? ''),
    contactNumber: String(company?.contactNumber ?? company?.ContactNumber ?? company?.phoneNumber ?? company?.PhoneNumber ?? company?.phone ?? company?.Phone ?? ''),
    streetAddress: String(company?.streetAddress ?? company?.StreetAddress ?? company?.address ?? company?.Address ?? ''),
    city: String(company?.city ?? company?.City ?? ''),
    state: String(company?.state ?? company?.State ?? ''),
    country: String(company?.country ?? company?.Country ?? 'United States'),
    deleted: Boolean(company?.deleted ?? company?.Deleted ?? false),
    isActive: Boolean(company?.isActive ?? company?.IsActive ?? true),
    isUsed: Boolean(company?.isUsed ?? company?.IsUsed ?? true),
    createdBy: company?.createdBy ?? company?.CreatedBy ?? null,
    createdDate: company?.createdDate ?? company?.CreatedDate ?? null,
    modifiedBy: company?.modifiedBy ?? company?.ModifiedBy ?? null,
    modifiedDate: company?.modifiedDate ?? company?.ModifiedDate ?? null
  };
};
const normalizeContactOption = contact => ({
  id: Number(contact?.id ?? contact?.Id ?? 0),
  firstName: String(contact?.firstName ?? contact?.FirstName ?? contact?.name ?? contact?.Name ?? ''),
  lastName: String(contact?.lastName ?? contact?.LastName ?? ''),
  phoneNumber: String(contact?.phoneNumber ?? contact?.PhoneNumber ?? contact?.contactNumber ?? contact?.ContactNumber ?? ''),
  country: String(contact?.country ?? contact?.Country ?? 'United States'),
  streetAddress: String(contact?.streetAddress ?? contact?.StreetAddress ?? ''),
  city: String(contact?.city ?? contact?.City ?? ''),
  state: String(contact?.state ?? contact?.State ?? ''),
  email: String(contact?.email ?? contact?.Email ?? ''),
  companyId: Number(contact?.companyId ?? contact?.CompanyId ?? contact?.company?.id ?? contact?.company?.Id ?? contact?.Company?.id ?? contact?.Company?.Id ?? 0)
});
const normalizeUser = user => ({
  id: Number(user?.id ?? user?.Id ?? user?.userId ?? user?.UserId ?? 0),
  firstName: String(user?.firstName ?? user?.FirstName ?? user?.name ?? user?.Name ?? ''),
  lastName: String(user?.lastName ?? user?.LastName ?? ''),
  fullName: String(user?.fullName ?? user?.FullName ?? `${user?.firstName ?? user?.FirstName ?? ''} ${user?.lastName ?? user?.LastName ?? ''}`.trim() ?? user?.name ?? user?.Name ?? ''),
  rolesSting: String(user?.rolesSting ?? user?.RolesSting ?? user?.rolesString ?? user?.RolesString ?? '')
});
const normalizeSalesRepresentative = representative => {
  const firstName = String(representative?.firstName ?? representative?.FirstName ?? '');
  const lastName = String(representative?.lastName ?? representative?.LastName ?? '');
  const fullName = String(representative?.fullName ?? representative?.FullName ?? representative?.name ?? representative?.Name ?? `${firstName} ${lastName}`.trim());
  return {
    id: Number(representative?.id ?? representative?.Id ?? representative?.saleRepId ?? representative?.SaleRepId ?? representative?.userId ?? representative?.UserId ?? 0),
    firstName: firstName || fullName.split(' ')[0] || '',
    lastName: lastName || fullName.split(' ').slice(1).join(' ') || '',
    fullName,
    userId: Number(representative?.userId ?? representative?.UserId ?? 0)
  };
};
const normalizeProject = project => {
  const id = Number(project?.id ?? project?.Id ?? 0);
  const companyId = Number(project?.companyId ?? project?.CompanyId ?? project?.company?.id ?? project?.company?.Id ?? project?.Company?.id ?? project?.Company?.Id ?? 0);
  const contactId = Number(project?.contactId ?? project?.ContactId ?? project?.contact?.id ?? project?.contact?.Id ?? project?.Contact?.id ?? project?.Contact?.Id ?? 0);
  const saleRepId = Number(project?.saleRepId ?? project?.SaleRepId ?? project?.saleRep?.id ?? project?.saleRep?.Id ?? project?.SaleRep?.id ?? project?.SaleRep?.Id ?? 0);
  const saleRep = project?.saleRep ?? project?.SaleRep;
  const contact = project?.contact ?? project?.Contact;
  const createdDate = project?.createdDate ?? project?.CreatedDate ?? project?.createdOn ?? project?.CreatedOn ?? null;
  const calculatedAge = calculateAgeInDays(createdDate);
  return {
    ...project,
    id,
    name: String(project?.name ?? project?.Name ?? ''),
    city: String(project?.city ?? project?.City ?? ''),
    state: String(project?.state ?? project?.State ?? ''),
    country: String(project?.country ?? project?.Country ?? 'United States'),
    isDomestic: Boolean(project?.isDomestic ?? project?.IsDomestic ?? true),
    saleRepId: saleRepId || null,
    companyId: companyId || null,
    contactId: contactId || null,
    market: String(project?.market ?? project?.Market ?? ''),
    confidence: project?.confidence ?? project?.Confidence ?? null,
    createdDate,
    closeDate: project?.closeDate ?? project?.CloseDate ?? null,
    statusId: Number(project?.statusId ?? project?.StatusId ?? 1) || 1,
    age: calculatedAge,
    ageFlag: Boolean(typeof calculatedAge === 'number' && calculatedAge > AGE_WARNING_DAYS),
    contactAddress: String(project?.contactAddress ?? project?.ContactAddress ?? ''),
    contactCity: String(project?.contactCity ?? project?.ContactCity ?? ''),
    contactState: String(project?.contactState ?? project?.ContactState ?? ''),
    contactName: String(project?.contactName ?? project?.ContactName ?? ''),
    contactPhoneNumber: String(project?.contactPhoneNumber ?? project?.ContactPhoneNumber ?? contact?.phoneNumber ?? contact?.PhoneNumber ?? ''),
    contactEmail: String(project?.contactEmail ?? project?.ContactEmail ?? contact?.email ?? contact?.Email ?? ''),
    company: normalizeCompany(project?.company ?? project?.Company, companyId || undefined),
    contact: contact ? normalizeContactOption(contact) : contactId ? {
      id: contactId,
      firstName: '',
      lastName: '',
      phoneNumber: '',
      country: 'United States'
    } : null,
    saleRep: {
      id: saleRepId || null,
      firstName: String(saleRep?.firstName ?? saleRep?.FirstName ?? ''),
      lastName: String(saleRep?.lastName ?? saleRep?.LastName ?? ''),
      fullName: String(saleRep?.fullName ?? saleRep?.FullName ?? `${saleRep?.firstName ?? saleRep?.FirstName ?? ''} ${saleRep?.lastName ?? saleRep?.LastName ?? ''}`.trim())
    }
  };
};
const normalizeQuote = quote => ({
  ...quote,
  id: Number(quote?.id ?? quote?.Id ?? 0),
  projectId: Number(quote?.projectId ?? quote?.ProjectId ?? 0),
  quotationNumber: String(quote?.quotationNumber ?? quote?.QuotationNumber ?? ''),
  modelNumber: String(quote?.modelNumber ?? quote?.ModelNumber ?? ''),
  status: String(quote?.status ?? quote?.Status ?? 'Draft'),
  statusId: Number(quote?.statusId ?? quote?.StatusId ?? 0),
  createdById: Number(quote?.createdById ?? quote?.CreatedById ?? 0),
  createdBy: String(quote?.createdBy ?? quote?.CreatedBy ?? ''),
  createdDate: quote?.createdDate ?? quote?.CreatedDate ?? null,
  finalPrice: Number(quote?.finalPrice ?? quote?.FinalPrice ?? 0),
  proposalUrl: quote?.proposalUrl ?? quote?.ProposalUrl ?? null
});
const toFiniteNumber = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};
const enrichProjectRelations = (project, companies, contacts, users, saleRepresentatives = []) => {
  const projectCompanyId = Number(project?.companyId ?? project?.CompanyId ?? project?.company?.id ?? project?.company?.Id ?? project?.Company?.id ?? project?.Company?.Id ?? 0);
  const existingCompany = normalizeCompany(project?.company ?? project?.Company, projectCompanyId || null);
  const lookupCompany = normalizeCompany(companies.find(item => Number(item?.id) === projectCompanyId), projectCompanyId || null);
  const company = existingCompany?.name ? existingCompany : lookupCompany?.name ? lookupCompany : existingCompany || lookupCompany;
  const contact = project?.contact?.id ? project.contact : normalizeContactOption(contacts.find(item => Number(item?.id) === Number(project?.contactId)));
  const saleRepFromRepresentative = saleRepresentatives.find(item => Number(item?.id) === Number(project?.saleRepId) || Number(item?.userId) === Number(project?.saleRepId));
  const saleRepFromUser = users.find(item => Number(item?.id) === Number(project?.saleRepId));
  const saleRepFullName = String(project?.saleRep?.fullName || project?.saleRep?.FullName || saleRepFromRepresentative?.fullName || saleRepFromUser?.fullName || `${saleRepFromUser?.firstName || ''} ${saleRepFromUser?.lastName || ''}`.trim() || project?.saleRep?.name || project?.saleRep?.Name || '');
  const normalizedContact = contact?.id ? contact : null;
  const normalizedCompany = company?.id ? company : null;
  return {
    ...project,
    company: normalizedCompany || project?.company || project?.Company || null,
    contact: normalizedContact || project?.contact || project?.Contact || null,
    contactName: project?.contactName || project?.ContactName || '',
    contactEmail: project?.contactEmail || project?.ContactEmail || normalizedContact?.email || '',
    contactPhoneNumber: project?.contactPhoneNumber || project?.ContactPhoneNumber || normalizedContact?.phoneNumber || '',
    saleRep: {
      ...project?.saleRep,
      ...project?.SaleRep,
      id: project?.saleRepId || saleRepFromRepresentative?.id || saleRepFromUser?.id || null,
      firstName: project?.saleRep?.firstName || project?.SaleRep?.firstName || project?.SaleRep?.FirstName || saleRepFromRepresentative?.firstName || saleRepFromUser?.firstName || '',
      lastName: project?.saleRep?.lastName || project?.SaleRep?.lastName || project?.SaleRep?.LastName || saleRepFromRepresentative?.lastName || saleRepFromUser?.lastName || '',
      fullName: saleRepFullName || (project?.saleRepId ? `Rep ${project.saleRepId}` : '')
    }
  };
};
const toArray = response => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.Data)) return response.Data;
  if (Array.isArray(response?.value)) return response.value;
  if (Array.isArray(response?.Value)) return response.Value;
  if (Array.isArray(response?.$values)) return response.$values;
  if (Array.isArray(response?.result)) return response.result;
  if (Array.isArray(response?.Result)) return response.Result;
  if (Array.isArray(response?.items)) return response.items;
  return [];
};
const emptyProjectForm = {
  id: 0,
  name: '',
  city: '',
  state: '',
  country: 'United States',
  isDomestic: true,
  saleRepId: null,
  companyId: null,
  contactId: null,
  market: '',
  confidence: null,
  closeDate: '',
  statusId: 1
};
const emptyCompanyForm = {
  id: 0,
  name: '',
  contactNumber: '',
  streetAddress: '',
  city: '',
  state: '',
  country: 'United States',
  deleted: false,
  isActive: true
};
const SalesQuoteList = () => {
  const apiUrls = useSelector(selectApiUrls);
  const ENDPOINTS = useMemo(() => createSalesEndpoints(apiUrls), [apiUrls]);
  const portalApiRequest = usePortalApiRequest();
  const apiRequest = useMemo(() => ({ method = 'GET', url, data }) => portalApiRequest({ method: String(method).toLowerCase(), url, data }), [portalApiRequest]);
  const { isAdmin: hasAdminRole } = usePermissions();
  const userDetails = useSelector(selectUserDetails);
  const userRoles = useSelector(selectUserRoles);
  const user = useMemo(() => ({
    id: Number(userDetails?.id ?? userDetails?.localAccountId ?? 0) || 0,
    roles: (userRoles || []).map((name) => ({ name })),
  }), [userDetails, userRoles]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedProjects, setExpandedProjects] = useState(new Set());
  const [projectRows, setProjectRows] = useState([]);
  const [quoteRows, setQuoteRows] = useState([]);
  const [editingStatusProjectId, setEditingStatusProjectId] = useState(null);
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedSalesRepId, setSelectedSalesRepId] = useState('');
  const [selectedCreatedById, setSelectedCreatedById] = useState('');
  const [showOpenOnly, setShowOpenOnly] = useState(false);
  const [showOnlyMe, setShowOnlyMe] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [statusOptions, setStatusOptions] = useState([{
    id: 1,
    label: 'Open'
  }, {
    id: 2,
    label: 'Won'
  }, {
    id: 3,
    label: 'Lost'
  }]);
  const [allStatusOptions, setAllStatusOptions] = useState([{
    id: 1,
    label: 'Open'
  }, {
    id: 2,
    label: 'Won'
  }, {
    id: 3,
    label: 'Lost'
  }]);
  const [statusUpdateError, setStatusUpdateError] = useState(null);
  const [savingStatusProjectId, setSavingStatusProjectId] = useState(null);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [isEditProjectModal, setIsEditProjectModal] = useState(false);
  const [projectForm, setProjectForm] = useState(emptyProjectForm);
  const [projectFormError, setProjectFormError] = useState(null);
  const [savingProjectForm, setSavingProjectForm] = useState(false);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [contactOptions, setContactOptions] = useState([]);
  const [salesRepFormOptions, setSalesRepFormOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState(fallbackStates);
  const [marketOptions, setMarketOptions] = useState(fallbackMarkets);
  const [confidenceOptions, setConfidenceOptions] = useState(fallbackConfidence);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [companyForm, setCompanyForm] = useState(emptyCompanyForm);
  const [companyFormError, setCompanyFormError] = useState(null);
  const [savingCompanyForm, setSavingCompanyForm] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingTarget, setDeletingTarget] = useState(false);
  const isAdmin = hasAdminRole();
  const displayProjects = projectRows;
  const displayQuotes = quoteRows;
  const getProjectContactDisplayName = project => {
    const fromContact = `${project?.contact?.firstName || ''} ${project?.contact?.lastName || ''}`.trim();
    if (fromContact) return fromContact;
    if (project?.contactName) return String(project.contactName);
    if (project?.contactEmail) return String(project.contactEmail);
    if (project?.contactId) return `Contact ${project.contactId}`;
    return '';
  };
  const getProjectCompanyDisplayName = project => {
    if (project?.company?.name) return String(project.company.name);
    if (project?.companyName) return String(project.companyName);
    if (project?.CompanyName) return String(project.CompanyName);
    if (project?.companyId) {
      const byOption = companyOptions.find(item => Number(item?.id) === Number(project.companyId));
      if (byOption?.name) return String(byOption.name);
    }
    if (project?.companyId) return `Company ${project.companyId}`;
    return '';
  };
  const getProjectSalesRepDisplayName = project => {
    if (project?.saleRep?.fullName) return String(project.saleRep.fullName);
    if (project?.saleRepId) return `Rep ${project.saleRepId}`;
    return '';
  };
  const salesRepOptions = useMemo(() => {
    const unique = new Map();
    displayProjects.forEach(project => {
      if (project?.saleRepId && project?.saleRep?.fullName) {
        unique.set(project.saleRepId, project.saleRep.fullName);
      }
    });
    return Array.from(unique.entries()).map(([id, name]) => ({
      id,
      name
    }));
  }, [displayProjects]);
  const createdByOptions = useMemo(() => {
    const unique = new Map();
    displayQuotes.forEach(quote => {
      if (quote?.createdById && quote?.createdBy) {
        unique.set(quote.createdById, quote.createdBy);
      }
    });
    return Array.from(unique.entries()).map(([id, name]) => ({
      id,
      name
    }));
  }, [displayQuotes]);
  useEffect(() => {
    let isCurrentRequest = true;
    const loadProjectsAndQuotes = async () => {
      setIsLoading(true);
      setError('');
      try {
        const [projectsResponse, quotesResponse, companiesResponse, contactsResponse, usersResponse, saleRepresentativesResponse, costSummariesResponse, lookupsResponse] = await Promise.all([apiRequest({
          method: 'GET',
          url: ENDPOINTS.projects.base
        }).catch(() => ({
          data: []
        })), apiRequest({
          method: 'GET',
          url: ENDPOINTS.salesQuotes.base
        }).catch(() => ({
          data: []
        })), apiRequest({
          method: 'GET',
          url: ENDPOINTS.companies.base
        }).catch(() => ({
          data: []
        })), apiRequest({
          method: 'GET',
          url: ENDPOINTS.contacts.base
        }).catch(() => ({
          data: []
        })), apiRequest({
          method: 'GET',
          url: ENDPOINTS.users.base
        }).catch(() => ({
          data: []
        })), apiRequest({
          method: 'GET',
          url: ENDPOINTS.saleRepresentatives.base
        }).catch(() => ({
          data: []
        })), apiRequest({
          method: 'GET',
          url: ENDPOINTS.costSummaries.base
        }).catch(() => ({
          data: []
        })), apiRequest({
          method: 'GET',
          url: ENDPOINTS.lookups.base
        }).catch(() => ({
          data: []
        }))]);
        const companies = toArray(companiesResponse).map(company => normalizeCompany(company));
        const contacts = toArray(contactsResponse).map(contact => normalizeContactOption(contact));
        const users = toArray(usersResponse).map(item => normalizeUser(item));
        const saleRepresentatives = toArray(saleRepresentativesResponse).map(item => normalizeSalesRepresentative(item)).filter(item => item.id);
        const projects = toArray(projectsResponse)
          .filter(item => !isDeletedSalesRow(item))
          .map(item => normalizeProject(item))
          .map(project => enrichProjectRelations(project, companies, contacts, users, saleRepresentatives));
        const lookups = toArray(lookupsResponse).filter(lookup => lookup?.isActive !== false);
        const mappedQuoteStatus = lookups.map(lookup => {
          const directTypeId = Number(lookup?.lookupTypeId ?? lookup?.LookupTypeId ?? 0);
          const nestedTypeId = Number(lookup?.lookupType?.id ?? lookup?.lookupType?.Id ?? lookup?.LookupType?.id ?? lookup?.LookupType?.Id ?? 0);
          if (directTypeId !== SALES_QUOTE_STATUS_LOOKUP_TYPE_ID && nestedTypeId !== SALES_QUOTE_STATUS_LOOKUP_TYPE_ID) {
            return null;
          }
          const valueKey = Number(lookup?.value ?? lookup?.Value);
          const idKey = Number(lookup?.id ?? lookup?.Id);
          const statusKey = Number.isFinite(valueKey) ? valueKey : idKey;
          const label = String(lookup?.name ?? lookup?.Name ?? lookup?.description ?? lookup?.Description ?? lookup?.label ?? lookup?.Label ?? '').trim();
          if (!Number.isFinite(statusKey) || !label) {
            return null;
          }
          return {
            id: statusKey,
            label
          };
        }).filter(Boolean);
        const quoteStatusLookup = mappedQuoteStatus.reduce((acc, current) => {
          acc[current.id] = current.label;
          return acc;
        }, {});
        const costSummaries = toArray(costSummariesResponse);
        const finalPriceByQuoteId = new Map();
        costSummaries.forEach(row => {
          const saleQuoteId = Number(row?.saleQuoteId ?? row?.SaleQuoteId ?? 0);
          if (!saleQuoteId) {
            return;
          }
          const finalPrice = toFiniteNumber(row?.finalSalePrice ?? row?.FinalSalePrice ?? row?.totalCost ?? row?.TotalCost ?? row?.totalPrice ?? row?.TotalPrice, 0);
          finalPriceByQuoteId.set(saleQuoteId, finalPrice);
        });
        const userIdNum = user?.id ? Number(user.id) : 0;
        const quotes = (userIdNum > 0 ? toArray(quotesResponse).filter(q => Number(q?.createdBy ?? q?.createdById ?? q?.userId ?? 0) === userIdNum) : toArray(quotesResponse)).map(item => {
          const normalized = normalizeQuote(item);
          const mappedStatus = quoteStatusLookup[Number(normalized.statusId)] || normalized.status;
          const mappedFinalPrice = finalPriceByQuoteId.has(normalized.id) ? finalPriceByQuoteId.get(normalized.id) : normalized.finalPrice;
          return {
            ...normalized,
            status: mappedStatus,
            finalPrice: mappedFinalPrice
          };
        });
        const quotesByProject = new Map();
        quotes.forEach(quote => {
          const projectId = Number(quote?.projectId ?? 0);
          if (!quotesByProject.has(projectId)) {
            quotesByProject.set(projectId, []);
          }
          quotesByProject.get(projectId)?.push(quote);
        });
        const projectsWithDerivedAge = projects.map(project => applyDerivedProjectAge(project, quotesByProject.get(Number(project?.id ?? 0)) || []));
        if (!isCurrentRequest) {
          return;
        }
        setProjectRows(projectsWithDerivedAge);
        setQuoteRows(quotes);
      } catch (loadError) {
        if (!isCurrentRequest) {
          return;
        }
        console.error('Failed to load Sales Quote data', loadError);
        setProjectRows([]);
        setQuoteRows([]);
        setError('Sales Quote data could not be loaded. Please refresh and try again.');
      } finally {
        if (isCurrentRequest) {
          setIsLoading(false);
        }
      }
    };
    loadProjectsAndQuotes();
    return () => {
      isCurrentRequest = false;
    };
  }, [apiRequest, ENDPOINTS, user?.id]);
  useEffect(() => {
    const loadStatusLookups = async () => {
      try {
        const response = await apiRequest({
          method: 'GET',
          url: ENDPOINTS.lookups.base
        });
        const lookups = toArray(response).filter(lookup => lookup?.isActive !== false);
        if (lookups.length === 0) {
          return;
        }
        const mappedProjectStatus = lookups.map(lookup => {
          const lookupId = Number(lookup.id ?? lookup.Id);
          const rawValue = lookup.value ?? lookup.Value;
          const parsedValue = Number(rawValue);
          const statusKey = !Number.isNaN(parsedValue) && Number.isFinite(parsedValue) ? parsedValue : lookupId;
          return {
            id: statusKey,
            lookupId,
            label: String(lookup.name ?? lookup.Name ?? lookup.description ?? lookup.Description ?? lookup.label ?? lookup.Label ?? lookup.value ?? lookup.Value ?? `Status ${lookup.id ?? lookup.Id}`),
            lookupTypeId: Number(lookup.lookupTypeId ?? lookup.LookupTypeId ?? 0),
            typeName: String(lookup?.lookupType?.typeName || lookup?.lookupType?.TypeName || lookup?.lookupType?.name || lookup?.lookupType?.Name || lookup?.lookupTypeName || lookup?.LookupTypeName || lookup?.typeName || lookup?.TypeName || ''),
            nestedLookupTypeId: Number(lookup?.lookupType?.id ?? lookup?.lookupType?.Id ?? lookup?.LookupType?.id ?? lookup?.LookupType?.Id ?? 0)
          };
        }).filter(option => !Number.isNaN(option.id) && (option.lookupTypeId === PROJECT_STATUS_LOOKUP_TYPE_ID || option.nestedLookupTypeId === PROJECT_STATUS_LOOKUP_TYPE_ID));
        const dedupedProjectStatus = Array.from(new Map([...LEGACY_PROJECT_STATUS_OPTIONS, ...mappedProjectStatus].map(option => [option.id, option])).values());
        const allOptions = dedupedProjectStatus.map(option => ({
          id: option.id,
          label: option.label
        }));
        const editOptions = dedupedProjectStatus.map(option => ({
          id: option.id,
          label: option.label
        })).filter(option => !option.label.toLowerCase().includes('sold'));
        if (allOptions.length > 0) {
          setAllStatusOptions(allOptions);
        }
        if (editOptions.length > 0) {
          setStatusOptions(editOptions);
        }
      } catch {}
    };
    loadStatusLookups();
  }, [apiRequest, ENDPOINTS]);
  useEffect(() => {
    const loadProjectFormData = async () => {
      try {
        const [companiesResponse, usersResponse, saleRepresentativesResponse, statesResponse, marketResponse, confidenceResponse] = await Promise.all([apiRequest({
          method: 'GET',
          url: ENDPOINTS.companies.base
        }).catch(() => ({
          data: []
        })), apiRequest({
          method: 'GET',
          url: ENDPOINTS.users.base
        }).catch(() => ({
          data: []
        })), apiRequest({
          method: 'GET',
          url: ENDPOINTS.saleRepresentatives.base
        }).catch(() => ({
          data: []
        })), apiRequest({
          method: 'GET',
          url: ENDPOINTS.lookups.base
        }).catch(() => ({
          data: []
        })), apiRequest({
          method: 'GET',
          url: ENDPOINTS.lookups.base
        }).catch(() => ({
          data: []
        })), apiRequest({
          method: 'GET',
          url: ENDPOINTS.lookups.base
        }).catch(() => ({
          data: []
        }))]);
        const companies = toArray(companiesResponse).map(company => normalizeCompany(company));
        const users = toArray(usersResponse).map(u => normalizeUser(u));
        const saleRepresentatives = toArray(saleRepresentativesResponse).map(item => normalizeSalesRepresentative(item)).filter(item => item.id);
        const states = toArray(statesResponse).filter(item => String(item?.lookupType?.name ?? item?.typeName ?? '').toLowerCase() === 'states').map(item => item?.name ?? item?.Name).filter(Boolean);
        const markets = toArray(marketResponse).filter(item => String(item?.lookupType?.name ?? item?.typeName ?? '').toLowerCase() === 'market').map(item => item?.name ?? item?.Name).filter(Boolean);
        const confidences = toArray(confidenceResponse).filter(item => String(item?.lookupType?.name ?? item?.typeName ?? '').toLowerCase() === 'confidence').map(item => Number(item?.name ?? item?.Name ?? item?.value ?? item?.Value)).filter(value => !Number.isNaN(value));
        if (companies.length > 0) {
          setCompanyOptions(companies);
        }
        if (saleRepresentatives.length > 0) {
          setSalesRepFormOptions(saleRepresentatives);
        } else if (users.length > 0) {
          const salesReps = users.filter(u => String(u.rolesSting || '').toLowerCase().includes('sale representative') || String(u.rolesSting || '').toLowerCase().includes('sales'));
          setSalesRepFormOptions(salesReps.length > 0 ? salesReps : users);
        }
        if (states.length > 0) {
          setStateOptions(states);
        }
        if (markets.length > 0) {
          setMarketOptions(markets);
        }
        if (confidences.length > 0) {
          setConfidenceOptions(confidences);
        }
      } catch {}
    };
    loadProjectFormData();
  }, [apiRequest, ENDPOINTS]);
  useEffect(() => {
    if (companyOptions.length === 0) {
      const fallbackCompanies = displayProjects.map(project => normalizeCompany(project.company, project.companyId)).filter(company => company?.id && company?.name).filter((company, index, array) => array.findIndex(item => item.id === company.id) === index);
      setCompanyOptions(fallbackCompanies);
    }
    if (salesRepFormOptions.length === 0) {
      const fallbackReps = displayProjects.map(project => ({
        id: project.saleRepId || project.saleRep?.id,
        firstName: project.saleRep?.firstName || (project.saleRep?.fullName || '').split(' ')[0] || '',
        lastName: project.saleRep?.lastName || (project.saleRep?.fullName || '').split(' ').slice(1).join(' ') || ''
      })).filter(rep => rep.id).filter((rep, index, array) => array.findIndex(item => item.id === rep.id) === index);
      setSalesRepFormOptions(fallbackReps);
    }
  }, [companyOptions.length, displayProjects, salesRepFormOptions.length]);
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedSalesRepId, selectedCreatedById, showOpenOnly, showOnlyMe, rowsPerPage]);
  const quotesByProject = useMemo(() => {
    const grouped = new Map();
    displayQuotes.forEach(quote => {
      if (!grouped.has(quote.projectId)) {
        grouped.set(quote.projectId, []);
      }
      grouped.get(quote.projectId).push(quote);
    });
    return grouped;
  }, [displayQuotes]);
  const resolveProjectStatusLabel = statusId => {
    const found = allStatusOptions.find(option => option.id === statusId) || statusOptions.find(option => option.id === statusId);
    if (found) {
      return found.label;
    }
    return `Status ${statusId}`;
  };
  const filteredProjects = (() => {
    const term = searchTerm.toLowerCase();
    return displayProjects.filter(project => {
      if (showOpenOnly) {
        const statusLabel = resolveProjectStatusLabel(Number(project.statusId)).toLowerCase();
        if (statusLabel.includes('sold') || statusLabel.includes('lost')) {
          return false;
        }
      }
      if (selectedSalesRepId !== '' && project.saleRepId !== selectedSalesRepId) {
        return false;
      }
      if (showOnlyMe && !isAdmin && user?.id && project.saleRepId !== user.id) {
        return false;
      }
      if (selectedCreatedById !== '') {
        const childQuotes = quotesByProject.get(project.id) || [];
        const createdByMatch = childQuotes.some(quote => quote.createdById === selectedCreatedById);
        if (!createdByMatch) {
          return false;
        }
      }
      if (!term) {
        return true;
      }
      const companyName = getProjectCompanyDisplayName(project);
      const contactName = getProjectContactDisplayName(project);
      const salesRepName = getProjectSalesRepDisplayName(project);
      const parentMatch = [project.name, companyName, contactName, project.contactEmail || '', project.contactPhoneNumber || '', salesRepName, project.state || '', String(project.age ?? '')].some(value => value.toLowerCase().includes(term));
      if (parentMatch) {
        return true;
      }
      const childQuotes = quotesByProject.get(project.id) || [];
      return childQuotes.some(quote => quote.quotationNumber.toLowerCase().includes(term) || quote.modelNumber.toLowerCase().includes(term) || quote.status.toLowerCase().includes(term) || quote.createdBy.toLowerCase().includes(term));
    });
  })();
  const toggleProjectExpansion = projectId => {
    const next = new Set(expandedProjects);
    if (next.has(projectId)) {
      next.delete(projectId);
    } else {
      next.add(projectId);
    }
    setExpandedProjects(next);
  };
  const handleProjectStatusChange = async (projectId, newStatusId) => {
    const original = projectRows.find(row => row.id === projectId);
    if (!original) {
      setEditingStatusProjectId(null);
      return;
    }
    setStatusUpdateError(null);
    setSavingStatusProjectId(projectId);
    setProjectRows(prevRows => prevRows.map(row => row.id === projectId ? {
      ...row,
      statusId: newStatusId
    } : row));
    try {
      await apiRequest({
        method: 'PUT',
        url: ENDPOINTS.projects.byId(projectId),
        data: {
          ...original,
          statusId: newStatusId
        }
      });
      setEditingStatusProjectId(null);
    } catch {
      setProjectRows(prevRows => prevRows.map(row => row.id === projectId ? {
        ...row,
        statusId: original.statusId
      } : row));
      setStatusUpdateError('Unable to update project status right now.');
      setEditingStatusProjectId(null);
    } finally {
      setSavingStatusProjectId(null);
    }
  };
  const clearFilters = () => {
    setSelectedSalesRepId('');
    setSelectedCreatedById('');
    setShowOpenOnly(false);
    setShowOnlyMe(false);
    setSearchTerm('');
  };
  const openDeleteProjectModal = (event, project) => {
    event.preventDefault();
    setDeleteTarget({
      type: 'project',
      id: project.id,
      label: project.name || `Project ${project.id}`
    });
    setShowDeleteConfirmModal(true);
  };
  const openDeleteQuoteModal = (event, quote) => {
    event.preventDefault();
    setDeleteTarget({
      type: 'quote',
      id: quote.id,
      label: quote.quotationNumber || `Quote ${quote.id}`
    });
    setShowDeleteConfirmModal(true);
  };
  const closeDeleteConfirmModal = () => {
    if (deletingTarget) return;
    setShowDeleteConfirmModal(false);
    setDeleteTarget(null);
  };
  const confirmDeleteTarget = async () => {
    if (!deleteTarget) return;
    setDeletingTarget(true);
    try {
      if (deleteTarget.type === 'project') {
        setProjectRows(prevRows => prevRows.filter(project => project.id !== deleteTarget.id));
        setQuoteRows(prevRows => prevRows.filter(quote => quote.projectId !== deleteTarget.id));
        setExpandedProjects(prev => {
          const next = new Set(prev);
          next.delete(deleteTarget.id);
          return next;
        });
        try {
          await apiRequest({
            method: 'DELETE',
            url: ENDPOINTS.projects.byId(deleteTarget.id)
          });
        } catch {}
      } else {
        setQuoteRows(prevRows => prevRows.filter(quote => quote.id !== deleteTarget.id));
        try {
          await apiRequest({
            method: 'DELETE',
            url: ENDPOINTS.salesQuotes.byId(deleteTarget.id)
          });
        } catch {}
      }
    } finally {
      setDeletingTarget(false);
      setShowDeleteConfirmModal(false);
      setDeleteTarget(null);
    }
  };
  const loadContactsForCompany = async (companyId, selectedContactId) => {
    if (!companyId) {
      setContactOptions([]);
      return [];
    }
    try {
      const response = await apiRequest({
        method: 'GET',
        url: ENDPOINTS.contacts.base
      });
      const companyIdNumber = Number(companyId);
      const selectedContactIdNumber = Number(selectedContactId);
      const contacts = toArray(response).filter(item => Number(item?.companyId ?? 0) === companyIdNumber).map(item => normalizeContactOption(item));
      setContactOptions(contacts);
      if (contacts.length > 0) {
        const matchedContact = selectedContactIdNumber ? contacts.find(item => Number(item.id) === selectedContactIdNumber) : null;
        setProjectForm(prev => ({
          ...prev,
          contactId: matchedContact?.id || contacts[0].id
        }));
      }
      return contacts;
    } catch {
      setContactOptions([]);
      return [];
    }
  };
  const openProjectModal = async project => {
    const isEdit = !!project;
    setIsEditProjectModal(isEdit);
    setProjectFormError(null);
    if (isEdit) {
      const formValue = {
        ...emptyProjectForm,
        id: project.id,
        name: project.name || '',
        city: project.city || '',
        state: project.state || '',
        country: project.country || 'United States',
        isDomestic: project.isDomestic !== false,
        saleRepId: project.saleRepId || project.saleRep?.id || null,
        companyId: project.companyId || project.company?.id || null,
        contactId: project.contactId || project.contact?.id || null,
        market: project.market || '',
        confidence: project.confidence ?? null,
        closeDate: project.closeDate ? String(project.closeDate).slice(0, 10) : '',
        statusId: project.statusId || 1
      };
      setProjectForm(formValue);
      if (formValue.companyId) {
        const loadedContacts = await loadContactsForCompany(formValue.companyId, formValue.contactId);
        if (project?.contact && loadedContacts.length === 0) {
          const normalizedContact = normalizeContactOption(project.contact);
          setContactOptions([normalizedContact]);
          setProjectForm(prev => ({
            ...prev,
            contactId: normalizedContact.id || prev.contactId
          }));
        }
      } else {
        setContactOptions(project?.contact ? [normalizeContactOption(project.contact)] : []);
      }
    } else {
      setProjectForm({
        ...emptyProjectForm,
        saleRepId: user?.id || null
      });
      setContactOptions([]);
    }
    setShowProjectModal(true);
  };
  const closeProjectModal = () => {
    setShowProjectModal(false);
    setProjectFormError(null);
    setSavingProjectForm(false);
  };
  const onProjectFormFieldChange = (field, value) => {
    setProjectForm(prev => {
      const next = {
        ...prev,
        [field]: value
      };
      if (field === 'isDomestic') {
        if (value) {
          next.country = 'United States';
        }
      }
      return next;
    });
  };
  const onProjectCompanyChange = async companyId => {
    setProjectForm(prev => ({
      ...prev,
      companyId,
      contactId: null
    }));
    await loadContactsForCompany(companyId);
  };
  const openCompanyModal = () => {
    setCompanyForm({
      ...emptyCompanyForm
    });
    setCompanyFormError(null);
    setShowCompanyModal(true);
  };
  const handleOpenCompanyModal = event => {
    event.preventDefault();
    event.stopPropagation();
    openCompanyModal();
  };
  const closeCompanyModal = () => {
    setShowCompanyModal(false);
    setCompanyFormError(null);
    setSavingCompanyForm(false);
  };
  const onCompanyFormFieldChange = (field, value) => {
    setCompanyForm(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const validateCompanyForm = () => {
    if (!companyForm.name?.trim()) return 'Please enter company name';
    if (companyForm.name.trim().length < 3) return 'Company name must be longer than 3 characters';
    if (!companyForm.contactNumber?.trim()) return 'Please enter contact number';
    if (!/^[+ ]?(\d{3}[- ]?){2}\d{4}$/.test(companyForm.contactNumber.trim())) return 'Please enter a valid contact number';
    if (!companyForm.city?.trim()) return 'Please enter city';
    if (!companyForm.state?.trim()) return 'Please select state';
    if (!companyForm.country?.trim()) return 'Please enter country';
    const duplicateName = companyOptions.some(company => String(company.name || '').toLowerCase() === String(companyForm.name || '').trim().toLowerCase());
    if (duplicateName) return 'Company name must be unique';
    return null;
  };
  const saveCompanyForm = async () => {
    const validationError = validateCompanyForm();
    if (validationError) {
      setCompanyFormError(validationError);
      return;
    }
    setCompanyFormError(null);
    setSavingCompanyForm(true);
    const payload = normalizeCompany(companyForm);
    try {
      const response = await apiRequest({
        method: 'POST',
        url: ENDPOINTS.companies.base,
        data: payload
      });
      const createdCompany = normalizeCompany(response?.data ? {
        ...payload,
        ...response.data
      } : {
        ...payload,
        id: Date.now()
      });
      if (!createdCompany) {
        throw new Error('Company creation failed');
      }
      setCompanyOptions(prev => [createdCompany, ...prev]);
      setProjectForm(prev => ({
        ...prev,
        companyId: createdCompany.id,
        contactId: null
      }));
      setContactOptions([]);
      closeCompanyModal();
    } catch {
      const createdCompany = normalizeCompany({
        ...payload,
        id: Date.now()
      });
      if (!createdCompany) {
        setCompanyFormError('Unable to create company right now.');
        setSavingCompanyForm(false);
        return;
      }
      setCompanyOptions(prev => [createdCompany, ...prev]);
      setProjectForm(prev => ({
        ...prev,
        companyId: createdCompany.id,
        contactId: null
      }));
      setContactOptions([]);
      closeCompanyModal();
    } finally {
      setSavingCompanyForm(false);
    }
  };
  const validateProjectForm = () => {
    if (!projectForm.name?.trim()) return 'Please enter project name';
    if (!projectForm.city?.trim()) return 'Please enter city';
    if (projectForm.isDomestic && !projectForm.state) return 'Please select state';
    if (!projectForm.country?.trim()) return 'Please enter country';
    if (!projectForm.saleRepId) return 'Please select Sales Rep';
    if (!projectForm.companyId) return 'Please select company';
    if (!projectForm.contactId) return 'Please select contact';
    if (!projectForm.market) return 'Please select Market';
    if (projectForm.confidence === null || projectForm.confidence === undefined || projectForm.confidence === '') return 'Please select Confidence';
    return null;
  };
  const saveProjectForm = async () => {
    const validationError = validateProjectForm();
    if (validationError) {
      setProjectFormError(validationError);
      return;
    }
    setProjectFormError(null);
    setSavingProjectForm(true);
    const selectedCompany = normalizeCompany(companyOptions.find(company => company.id === projectForm.companyId), projectForm.companyId);
    const selectedContactRaw = contactOptions.find(contact => contact.id === projectForm.contactId);
    const selectedContact = selectedContactRaw ? {
      ...selectedContactRaw,
      company: selectedCompany,
      companyId: selectedCompany?.id || selectedContactRaw.companyId || null
    } : null;
    const selectedSalesRep = salesRepFormOptions.find(rep => rep.id === projectForm.saleRepId);
    const payload = {
      ...projectForm,
      contact: selectedContact || null,
      company: selectedCompany || null,
      saleRep: selectedSalesRep || null,
      contactAddress: selectedContact?.streetAddress || '',
      contactCity: selectedContact?.city || null,
      contactState: selectedContact?.state || null,
      contactPhoneNumber: selectedContact?.phoneNumber || '',
      contactEmail: selectedContact?.email || '',
      closeDate: projectForm.closeDate || null
    };
    try {
      if (isEditProjectModal) {
        await apiRequest({
          method: 'PUT',
          url: ENDPOINTS.projects.byId(projectForm.id),
          data: payload
        });
        setProjectRows(prevRows => prevRows.map(row => row.id === projectForm.id ? normalizeProject({
          ...row,
          ...payload
        }) : row));
      } else {
        const response = await apiRequest({
          method: 'POST',
          url: ENDPOINTS.projects.base,
          data: payload
        });
        const createdProject = normalizeProject(response?.data ? {
          ...payload,
          ...response.data
        } : {
          ...payload,
          id: Date.now()
        });
        setProjectRows(prevRows => [createdProject, ...prevRows]);
      }
      closeProjectModal();
    } catch {
      if (isEditProjectModal) {
        setProjectRows(prevRows => prevRows.map(row => row.id === projectForm.id ? normalizeProject({
          ...row,
          ...payload
        }) : row));
        closeProjectModal();
        return;
      }
      const createdProject = normalizeProject({
        ...payload,
        id: Date.now()
      });
      setProjectRows(prevRows => [createdProject, ...prevRows]);
      closeProjectModal();
    } finally {
      setSavingProjectForm(false);
    }
  };
  const handleSort = field => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      return;
    }
    setSortField(field);
    setSortDirection('asc');
  };
  const getSortIcon = field => {
    if (sortField !== field) {
      return <i className="bi bi-arrow-down-up ms-1"></i>;
    }
    return sortDirection === 'asc' ? <i className="bi bi-arrow-up ms-1"></i> : <i className="bi bi-arrow-down ms-1"></i>;
  };
  const getStatusOptionsForProject = statusId => {
    if (statusOptions.some(option => option.id === statusId)) {
      return statusOptions;
    }
    const fallback = allStatusOptions.find(option => option.id === statusId);
    if (fallback) {
      return [...statusOptions, fallback];
    }
    return [...statusOptions, {
      id: statusId,
      label: `Status ${statusId}`
    }];
  };
  const getProjectStatus = statusId => {
    return resolveProjectStatusLabel(statusId);
  };
  const sortedProjects = (() => {
    const rows = [...filteredProjects];
    rows.sort((a, b) => {
      const getValue = row => {
        switch (sortField) {
          case 'age':
            return row.age ?? -1;
          case 'status':
            return getProjectStatus(row.statusId);
          case 'name':
            return row.name || '';
          case 'company':
            return getProjectCompanyDisplayName(row);
          case 'contactName':
            return `${row.contact?.firstName || ''} ${row.contact?.lastName || ''}`.trim();
          case 'salesRep':
            return row.saleRep?.fullName || '';
          case 'state':
            return row.state || '';
          default:
            return row.name || '';
        }
      };
      const aValue = getValue(a);
      const bValue = getValue(b);
      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
    return rows;
  })();
  const pagedProjects = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return sortedProjects.slice(start, start + rowsPerPage);
  }, [currentPage, rowsPerPage, sortedProjects]);
  const firstRecord = sortedProjects.length === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const lastRecord = Math.min(currentPage * rowsPerPage, sortedProjects.length);
  const totalPages = Math.max(1, Math.ceil(sortedProjects.length / rowsPerPage));
  const isFiltered = selectedSalesRepId !== '' || selectedCreatedById !== '' || showOpenOnly || showOnlyMe || !!searchTerm;
  if (isLoading) {
    return <div className="d-flex flex-column justify-content-center align-items-center text-muted" style={{
      minHeight: '200px'
    }}>
        <Spinner animation="border" variant="primary" />
        <div className="mt-3">Loading Sales Quote data...</div>
      </div>;
  }
  return <div>
      <Row className="mb-3">
        <Col>
          <h4>Sales Quote List</h4>
        </Col>
      </Row>

      <Row className="mb-3 g-2 align-items-end">
        <Col md={3}>
          <div className="position-relative search-input">
            <i className="bi bi-search position-absolute" style={{
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#626262'
          }}></i>
            <input className="form-control" type="text" placeholder="Search" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{
            paddingLeft: '40px'
          }} />
          </div>
        </Col>
        <Col md={2}>
          <Form.Label className="mb-1">Search Sales Rep</Form.Label>
          <Form.Select value={selectedSalesRepId} onChange={e => setSelectedSalesRepId(e.target.value ? Number(e.target.value) : '')}>
            <option value="">All</option>
            {salesRepOptions.map(option => <option key={option.id} value={option.id}>{option.name}</option>)}
          </Form.Select>
        </Col>
        <Col md={2}>
          <Form.Label className="mb-1">Search Created By</Form.Label>
          <Form.Select disabled={showOpenOnly} value={selectedCreatedById} onChange={e => setSelectedCreatedById(e.target.value ? Number(e.target.value) : '')}>
            <option value="">All</option>
            {createdByOptions.map(option => <option key={option.id} value={option.id}>{option.name}</option>)}
          </Form.Select>
        </Col>
        <Col md={5} className="text-end">
          <Button variant="primary" className="me-2" disabled={!isFiltered} onClick={clearFilters}>Clear Filter</Button>
          <Button variant="primary" className="me-2" onClick={() => setShowOpenOnly(value => !value)}>
            {showOpenOnly ? 'Show All Projects' : 'Hide Sold-Lost Projects'}
          </Button>
          {!isAdmin && <Button variant="primary" className="me-2" onClick={() => setShowOnlyMe(value => !value)}>
              {showOnlyMe ? 'SHOW ALL' : 'ONLY ME'}
            </Button>}
          <Button variant="success" className="addbtnview" onClick={() => openProjectModal()}>
            <i className="bi bi-plus-circle me-2"></i>
            Add New Project
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}
      {statusUpdateError && <Alert variant="danger">{statusUpdateError}</Alert>}

      <div className="card">
        <div className="card-body p-0">
          <div className="table-responsive">
            <Table hover className="mb-0 salesquote-list-table">
              <thead>
                <tr>
                  <th style={{
                  width: '4%'
                }}></th>
                  <th style={{
                  width: '7%',
                  cursor: 'pointer'
                }} onClick={() => handleSort('age')}>Age {getSortIcon('age')}</th>
                  <th style={{
                  width: '10%',
                  cursor: 'pointer'
                }} onClick={() => handleSort('status')}>Status {getSortIcon('status')}</th>
                  <th style={{
                  width: '15%',
                  cursor: 'pointer'
                }} onClick={() => handleSort('name')}>Project Name {getSortIcon('name')}</th>
                  <th style={{
                  width: '15%',
                  cursor: 'pointer'
                }} onClick={() => handleSort('company')}>Company {getSortIcon('company')}</th>
                  <th style={{
                  width: '12%',
                  cursor: 'pointer'
                }} onClick={() => handleSort('contactName')}>Contact {getSortIcon('contactName')}</th>
                  <th style={{
                  width: '10%',
                  cursor: 'pointer'
                }} onClick={() => handleSort('salesRep')}>Sales Rep {getSortIcon('salesRep')}</th>
                  <th style={{
                  width: '7%',
                  cursor: 'pointer'
                }} onClick={() => handleSort('state')}>State {getSortIcon('state')}</th>
                  <th style={{
                  width: '8%',
                  textAlign: 'center'
                }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedProjects.length === 0 ? <tr>
                    <td colSpan={9}>
                      <div className="no-data">
                        {searchTerm ? 'No projects or quotes match your search' : 'No sales quotes found'}
                      </div>
                    </td>
                  </tr> : pagedProjects.map(project => {
                const isExpanded = expandedProjects.has(project.id);
                const projectQuotes = quotesByProject.get(project.id) || [];
                const contactName = getProjectContactDisplayName(project);
                const statusOptions = getStatusOptionsForProject(project.statusId);
                return <React.Fragment key={project.id}>
                        <tr>
                          <td>
                            <Button variant="link" size="sm" onClick={() => toggleProjectExpansion(project.id)} style={{
                        color: '#00457c',
                        textDecoration: 'none',
                        padding: '0',
                        fontSize: '1.1rem',
                        lineHeight: '1',
                        border: 'none',
                        background: 'transparent'
                      }} title={isExpanded ? 'Collapse' : 'Expand'}>
                              <i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'}`}></i>
                            </Button>
                          </td>
                          <td style={{
                      color: project.ageFlag ? '#dc3545' : undefined
                    }}>
                            {project.age ?? '-'}
                          </td>
                          <td onDoubleClick={() => {
                      if (project.statusId !== 3 && savingStatusProjectId !== project.id) {
                        setEditingStatusProjectId(project.id);
                      }
                    }} title="Double-click to edit status" style={{
                      cursor: project.statusId === 3 ? 'default' : 'pointer'
                    }}>
                            {editingStatusProjectId === project.id ? <select className="form-select form-select-sm" autoFocus disabled={savingStatusProjectId === project.id} value={project.statusId} onChange={e => handleProjectStatusChange(project.id, Number(e.target.value))} onBlur={() => setEditingStatusProjectId(null)}>
                                {statusOptions.map(option => <option key={option.id} value={option.id}>{option.label}</option>)}
                              </select> : getProjectStatus(project.statusId)}
                          </td>
                          <td><strong>{project.name}</strong></td>
                          <td>{getProjectCompanyDisplayName(project)}</td>
                          <td>{contactName}</td>
                          <td>{getProjectSalesRepDisplayName(project)}</td>
                          <td>{project.state || '-'}</td>
                          <td style={{
                      textAlign: 'center'
                    }}>
                            <div className="d-inline-flex align-items-center" style={{
                        gap: '10px'
                      }}>
                              <button type="button" className="edit-icon" title="Edit" style={{
                          display: 'inline-flex',
                          border: 0,
                          background: 'transparent',
                          padding: 0
                        }} onClick={() => openProjectModal(project)}>
                                <i className="bi bi-pencil-square"></i>
                              </button>
                              {isAdmin && <button type="button" className="edit-icon" title="Delete" style={{
                          display: 'inline-flex',
                          border: 0,
                          background: 'transparent',
                          padding: 0
                        }} onClick={(e) => openDeleteProjectModal(e, project)}>
                                  <i className="bi bi-trash"></i>
                                </button>}
                            </div>
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={9} style={{
                      padding: 0,
                      border: 'none'
                    }}>
                            {isExpanded && (
                              <div style={{
                          backgroundColor: '#f8f9fa',
                          padding: '0.75rem 1rem'
                        }}>
                                <SalesProjectSharePointTabs
                                  project={project}
                                  projectQuotes={projectQuotes}
                                  isAdmin={isAdmin}
                                  onDeleteQuote={openDeleteQuoteModal}
                                />
                              </div>
                            )}
                          </td>
                        </tr>
                      </React.Fragment>;
              })}
              </tbody>
            </Table>
          </div>
          <div className="d-flex justify-content-between align-items-center p-3 border-top">
            <div>
              Showing {firstRecord} to {lastRecord} of {sortedProjects.length} entries
            </div>
            <div className="d-flex align-items-center">
              <Form.Select size="sm" className="me-2" style={{
              width: '90px'
            }} value={rowsPerPage} onChange={e => setRowsPerPage(Number(e.target.value))}>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </Form.Select>
              <Button size="sm" variant="outline-secondary" className="me-2" disabled={currentPage === 1} onClick={() => setCurrentPage(page => Math.max(1, page - 1))}>Prev</Button>
              <span className="me-2">Page {currentPage} of {totalPages}</span>
              <Button size="sm" variant="outline-secondary" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(page => Math.min(totalPages, page + 1))}>Next</Button>
            </div>
          </div>
        </div>
      </div>

      <Modal show={showDeleteConfirmModal} onHide={closeDeleteConfirmModal} backdrop="static" centered>
        <Modal.Header closeButton={!deletingTarget}>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete {deleteTarget?.label ? `"${deleteTarget.label}"` : 'this item'}?
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="secondary" onClick={closeDeleteConfirmModal} disabled={deletingTarget}>Cancel</Button>
          <Button type="button" variant="danger" onClick={confirmDeleteTarget} disabled={deletingTarget}>
            {deletingTarget ? 'Deleting...' : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showProjectModal} onHide={closeProjectModal} size="xl" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>{isEditProjectModal ? 'Edit Project' : 'Add Project'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {projectFormError && <Alert variant="danger" className="mb-3">{projectFormError}</Alert>}

          <Row className="g-3">
            <Col md={12}>
              <Form.Check inline type="radio" name="projectType" id="projectTypeDomestic" label="Domestic" checked={projectForm.isDomestic === true} onChange={() => onProjectFormFieldChange('isDomestic', true)} />
              <Form.Check inline type="radio" name="projectType" id="projectTypeInternational" label="International" checked={projectForm.isDomestic === false} onChange={() => onProjectFormFieldChange('isDomestic', false)} />
            </Col>

            <Col md={6}>
              <Form.Label>Name *</Form.Label>
              <Form.Control value={projectForm.name} onChange={e => onProjectFormFieldChange('name', e.target.value)} placeholder="Project Name" />
            </Col>
            <Col md={6}>
              <Form.Label>City *</Form.Label>
              <Form.Control value={projectForm.city} onChange={e => onProjectFormFieldChange('city', e.target.value)} placeholder="City" />
            </Col>

            <Col md={6}>
              <Form.Label>State {projectForm.isDomestic ? '*' : ''}</Form.Label>
              <Form.Select value={projectForm.state || ''} onChange={e => onProjectFormFieldChange('state', e.target.value)}>
                <option value="">Select State</option>
                {stateOptions.map(state => <option key={state} value={state}>{state}</option>)}
              </Form.Select>
            </Col>
            <Col md={6}>
              <Form.Label>Country *</Form.Label>
              <Form.Control value={projectForm.country} disabled={projectForm.isDomestic} onChange={e => onProjectFormFieldChange('country', e.target.value)} placeholder="Country" />
            </Col>

            <Col md={6}>
              <Form.Label>SALES REP *</Form.Label>
              <Form.Select value={projectForm.saleRepId || ''} onChange={e => onProjectFormFieldChange('saleRepId', Number(e.target.value) || null)}>
                <option value="">Select Sales Rep</option>
                {salesRepFormOptions.map(rep => <option key={rep.id} value={rep.id}>{rep.firstName} {rep.lastName}</option>)}
              </Form.Select>
            </Col>
            <Col md={6}>
              <Form.Label>Company *</Form.Label>
              <div className="d-flex">
                <Form.Select value={projectForm.companyId || ''} onChange={e => onProjectCompanyChange(Number(e.target.value) || 0)}>
                  <option value="">Select Company</option>
                  {companyOptions.map(company => <option key={company.id} value={company.id}>{company.name}</option>)}
                </Form.Select>
                <Button type="button" variant="outline-primary" className="ms-2" onClick={handleOpenCompanyModal}>Add Company</Button>
              </div>
            </Col>

            <Col md={6}>
              <Form.Label>Market *</Form.Label>
              <Form.Select value={projectForm.market || ''} onChange={e => onProjectFormFieldChange('market', e.target.value)}>
                <option value="">Select Market</option>
                {marketOptions.map(market => <option key={market} value={market}>{market}</option>)}
              </Form.Select>
            </Col>
            <Col md={6}>
              <Form.Label>Confidence *</Form.Label>
              <Form.Select value={projectForm.confidence ?? ''} onChange={e => onProjectFormFieldChange('confidence', Number(e.target.value) || null)}>
                <option value="">Select Confidence</option>
                {confidenceOptions.map(confidence => <option key={confidence} value={confidence}>{confidence}</option>)}
              </Form.Select>
            </Col>
            <Col md={6}>
              <Form.Label>Close Date</Form.Label>
              <Form.Control type="date" value={projectForm.closeDate || ''} onChange={e => onProjectFormFieldChange('closeDate', e.target.value)} />
            </Col>

            <Col md={12}>
              <h6 className="mb-2">List of Contacts</h6>
              <div className="table-responsive border rounded">
                <Table bordered hover size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th style={{
                      width: '40px'
                    }}></th>
                      <th>First Name</th>
                      <th>Last Name</th>
                      <th>Phone Number</th>
                      <th>Country</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contactOptions.length === 0 ? <tr>
                        <td colSpan={5} className="text-center text-muted">No contacts found for selected company</td>
                      </tr> : contactOptions.map(contact => <tr key={contact.id}>
                          <td>
                            <Form.Check type="radio" name="selectedContact" checked={projectForm.contactId === contact.id} onChange={() => onProjectFormFieldChange('contactId', contact.id)} />
                          </td>
                          <td>{contact.firstName}</td>
                          <td>{contact.lastName}</td>
                          <td>{contact.phoneNumber}</td>
                          <td>{contact.country}</td>
                        </tr>)}
                  </tbody>
                </Table>
              </div>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="secondary" onClick={closeProjectModal} disabled={savingProjectForm}>Cancel</Button>
          <Button type="button" variant="primary" onClick={saveProjectForm} disabled={savingProjectForm}>
            {savingProjectForm ? 'Saving...' : isEditProjectModal ? 'Update' : 'Add'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showCompanyModal} onHide={closeCompanyModal} backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Add Company</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {companyFormError && <Alert variant="danger" className="mb-3">{companyFormError}</Alert>}
          <Row className="g-3">
            <Col md={12}>
              <Form.Label>Name *</Form.Label>
              <Form.Control value={companyForm.name} onChange={e => onCompanyFormFieldChange('name', e.target.value)} placeholder="Company Name" />
            </Col>
            <Col md={12}>
              <Form.Label>Contact Number *</Form.Label>
              <Form.Control value={companyForm.contactNumber} onChange={e => onCompanyFormFieldChange('contactNumber', e.target.value)} placeholder="262-367-5000" />
            </Col>
            <Col md={12}>
              <Form.Label>Street Address</Form.Label>
              <Form.Control value={companyForm.streetAddress} onChange={e => onCompanyFormFieldChange('streetAddress', e.target.value)} placeholder="Street Address" />
            </Col>
            <Col md={6}>
              <Form.Label>City *</Form.Label>
              <Form.Control value={companyForm.city} onChange={e => onCompanyFormFieldChange('city', e.target.value)} placeholder="City" />
            </Col>
            <Col md={6}>
              <Form.Label>State *</Form.Label>
              <Form.Select value={companyForm.state} onChange={e => onCompanyFormFieldChange('state', e.target.value)}>
                <option value="">Select State</option>
                {stateOptions.map(state => <option key={state} value={state}>{state}</option>)}
              </Form.Select>
            </Col>
            <Col md={12}>
              <Form.Label>Country *</Form.Label>
              <Form.Control value={companyForm.country} onChange={e => onCompanyFormFieldChange('country', e.target.value)} placeholder="Country" />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="secondary" onClick={closeCompanyModal} disabled={savingCompanyForm}>Cancel</Button>
          <Button type="button" variant="primary" onClick={saveCompanyForm} disabled={savingCompanyForm}>
            {savingCompanyForm ? 'Saving...' : 'Add'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>;
};
export default SalesQuoteList;
